import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Globe2, CheckCircle2 } from "lucide-react";
import { Reveal } from "../primitives/Reveal";
import { AccentTitle } from "../primitives/AccentTitle";

const EASE = [0.16, 1, 0.3, 1];

const RECORDS = [
    { id: "a1", type: "A", name: "@", value: "185.199.108.153", ttl: 300, kind: "A" },
    { id: "a2", type: "A", name: "@", value: "185.199.109.153", ttl: 300, kind: "A" },
    { id: "cname", type: "CNAME", name: "www", value: "thaqafa.sa.", ttl: 3600, kind: "CNAME" },
    { id: "mx", type: "MX", name: "@", value: "10 mail.nour.net.", ttl: 3600, kind: "MX" },
    { id: "spf", type: "TXT", name: "@", value: "v=spf1 include:nour.net ~all", ttl: 3600, kind: "TXT" },
];

const NEW_RECORD = {
    id: "dmarc", type: "TXT", name: "_dmarc",
    value: "v=DMARC1; p=quarantine; rua=mailto:dmarc@thaqafa.sa",
    ttl: 3600, kind: "DMARC",
};

export const DNSPreview = () => {
    const { t } = useTranslation();
    const [typed, setTyped] = useState("");
    const [records, setRecords] = useState(RECORDS);
    const [hovered, setHovered] = useState("a1");
    const [propagated, setPropagated] = useState(null);
    const [seconds, setSeconds] = useState(0);
    const sectionRef = useRef(null);
    const startedRef = useRef(false);

    useEffect(() => {
        if (!sectionRef.current) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting && !startedRef.current) {
                    startedRef.current = true;
                    runSequence();
                }
            });
        }, { threshold: 0.4 });
        io.observe(sectionRef.current);
        return () => io.disconnect();
    }, []);

    const runSequence = async () => {
        const target = NEW_RECORD.value;
        for (let i = 0; i <= target.length; i++) {
            await new Promise((r) => setTimeout(r, 22));
            setTyped(target.slice(0, i));
        }
        await new Promise((r) => setTimeout(r, 300));
        setRecords((rs) => [...rs, NEW_RECORD]);
        setHovered("dmarc");
        let s = 0;
        const timer = setInterval(() => {
            s += 1; setSeconds(s);
            if (s >= 8) { clearInterval(timer); setPropagated(s); }
        }, 180);
    };

    const activeRecord = records.find((r) => r.id === hovered) || records[0];
    const note = t(`home.dnsPreview.notes.${activeRecord.kind}`, { returnObjects: true });

    return (
        <section
            id="dns" ref={sectionRef}
            className="scroll-mt-24 bg-ink-900 py-24 lg:py-32"
            data-testid="dns-preview-section"
        >
            <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
                <Reveal className="max-w-3xl">
                    <p className="eyebrow text-souq-400" style={{ color: "#E59500" }}>{t("home.dnsPreview.kicker")}</p>
                    <AccentTitle
                        component="h2"
                        className="section-headline mt-5 text-white"
                        title={t("home.dnsPreview.title")}
                        accent={t("home.dnsPreview.titleAccent")}
                        accentColor="#E59500"
                    />
                    <p className="body-prose mt-6 max-w-2xl text-ink-300" style={{ color: "#C7CCD4" }}>
                        {t("home.dnsPreview.subtitle")}
                    </p>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6, ease: EASE }}
                            className="overflow-hidden rounded-card border-2 border-souq-400/30 bg-[#0F1112] shadow-pop"
                            data-testid="dns-preview-card"
                        >
                            <div className="flex items-center gap-2.5 border-b border-souq-400/20 bg-[#161819] px-4 py-3">
                                <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
                                <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
                                <span className="h-3 w-3 rounded-full bg-[#28C840]" />
                                <div className="ms-3 inline-flex h-7 items-center rounded-[6px] bg-[#0A0A0A] px-3 font-mono text-[12px] font-semibold text-souq-300 num-ltr">
                                    nour.net / dns / thaqafa.sa
                                </div>
                                <div className="ms-auto inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-souq-400">
                                    <span className="h-2 w-2 rounded-full bg-souq-400" style={{ animation: "pulse-soft 1.4s ease-in-out infinite" }} />
                                    {t("home.dnsPreview.liveLabel")}
                                </div>
                            </div>

                            <div className="flex items-center justify-between border-b border-souq-400/15 bg-[#0A0A0A] px-4 py-2.5 font-mono text-[13px] text-ink-400 num-ltr">
                                <span>{t("home.dnsPreview.zoneLabel")} <span className="font-bold text-white">thaqafa.sa</span></span>
                                <span className="text-ink-500">{records.length} {t("home.dnsPreview.recordsLabel")}</span>
                            </div>

                            <div className="px-1 py-1 bg-[#0A0A0A]">
                                <div className="grid grid-cols-12 px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-ink-500">
                                    <div className="col-span-2">Type</div>
                                    <div className="col-span-3">Name</div>
                                    <div className="col-span-6">Value</div>
                                    <div className="col-span-1 text-end">TTL</div>
                                </div>
                                {records.map((r) => {
                                    const active = hovered === r.id;
                                    return (
                                        <button
                                            type="button"
                                            key={r.id}
                                            onMouseEnter={() => setHovered(r.id)}
                                            onFocus={() => setHovered(r.id)}
                                            data-testid={`dns-record-${r.id}`}
                                            className={`grid w-full grid-cols-12 items-center border-t border-souq-400/10 px-3 py-3 text-start font-mono text-[13px] transition-colors duration-200 num-ltr ${active ? "bg-souq-400/10" : "hover:bg-white/5"}`}
                                        >
                                            <div className="col-span-2">
                                                <span className={`inline-flex h-6 items-center rounded-[4px] px-2 text-[11px] font-bold ${active ? "bg-souq-400 text-ink-900" : "bg-white/10 text-souq-300"}`}>
                                                    {r.type}
                                                </span>
                                            </div>
                                            <div className="col-span-3 truncate text-ink-300">{r.name}</div>
                                            <div className="col-span-6 truncate text-white">
                                                {r.id === "dmarc" && typed.length < NEW_RECORD.value.length ? (
                                                    <>{typed}<span className="ms-0.5 inline-block h-3.5 w-[7px] translate-y-0.5 bg-souq-400 animate-caret-blink" /></>
                                                ) : r.value}
                                            </div>
                                            <div className="col-span-1 text-end tabular-nums text-ink-500">{r.ttl}</div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex items-center justify-between border-t border-souq-400/20 bg-[#161819] px-4 py-3 text-[13px]">
                                <div className="flex items-center gap-2 text-ink-300">
                                    <Globe2 className="h-4 w-4" />
                                    {propagated ? (
                                        <span className="inline-flex items-center gap-1.5 text-white font-semibold">
                                            <CheckCircle2 className="h-4 w-4 text-souq-400" />
                                            {t("home.dnsPreview.propagatedLabel")} · <span className="num-ltr tabular-nums text-souq-300">{propagated}{t("home.dnsPreview.seconds")}</span>
                                        </span>
                                    ) : (
                                        <span>Propagation · <span className="num-ltr tabular-nums text-souq-300 font-semibold">{seconds}{t("home.dnsPreview.seconds")}</span></span>
                                    )}
                                </div>
                                <div className="font-mono text-[11px] text-ink-500 num-ltr">ns1.nour.net · ns2.nour.net</div>
                            </div>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-28 border-t-2 border-souq-400/40 pt-8">
                            <p className="font-mono text-[12px] font-bold uppercase tracking-[0.16em] text-souq-400 num-ltr">
                                {t("home.dnsPreview.zoneLabel")} · {activeRecord.type}
                            </p>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeRecord.id}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.24, ease: EASE }}
                                >
                                    <h3 className="mt-3 font-display text-[36px] font-bold leading-tight tracking-tight text-white">
                                        {note?.title || activeRecord.type}
                                    </h3>
                                    <p className="mt-5 text-[18px] leading-relaxed text-ink-300">{note?.desc || ""}</p>
                                    <pre className="mt-7 overflow-x-auto rounded-card border-2 border-souq-400/40 bg-[#0F1112] px-4 py-4 font-mono text-[13px] leading-relaxed text-white num-ltr">
                                        <span className="text-souq-400 font-bold">{activeRecord.type.padEnd(6)}</span>
                                        <span className="text-ink-300">{activeRecord.name.padEnd(8)}</span>
                                        <span>{activeRecord.value}</span>
                                    </pre>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
